import { Project, ProjectAPI, TeamAPI, API, Endpoints, Lang } from "@brg/api";
import { useAuth, useProfile } from "@brg/auth";
import { useModal } from "core"
import { useToasts } from "@brg/ui"
import { useHistory } from "react-router-dom"
import React, { useCallback, useMemo } from "react";
import { fetchTestItem, unwrapTestItemPayload } from "shared/api/testItem";
import { buildLongreadTestResultsModalArgsFromTestItem, mergeTestItemForResultsModal } from "shared/modals/longreadTestResults/buildLongreadTestResultsModalArgsFromTestItem";
import { teachingEvaluationApi } from 'shared/pages/CourseEdit/teachingEvaluation/api';
import urlApi from "shared/actions/urlApi";

interface IProps {
  id: string;
  isTeamLg?: boolean;
  raw: any;
  feedShareTitle?: string;
  longreadId?: string;
}

export const useGetExternalLGHandlers = ({
  raw,
  id,
  isTeamLg,
  feedShareTitle,
  longreadId,
}: IProps) => {
  const modal = useModal();
  const toasts = useToasts();
  const history = useHistory();
  const { token, login } = useAuth();
  const profile = useProfile();

  const onOpenSetProjectsModal = useCallback(async (props: any) => {
    if (isTeamLg) {
      modal.show('send-project-longread', {
        ...props,
        hideBookmark: true,
      })
      return;
    }
    if (raw) {
      await ProjectAPI.Get({ idProject: raw?.projectId ?? id })
        .then(
          (value) => value.json() as Promise<Project<'Global'> & { creatorId: string }>
        )
        .then((value) => {
          if (value.projectId) {
            modal.show('send-project-longread', {
              ...props,
              project: value,
              hideBookmark: true,
              ...(isTeamLg
                ? {
                    externalParams: {
                      authorOrCoAuthorIds: [],
                    },
                  }
                : {}),
            })
          }
        })
    }
  }, [raw, isTeamLg])

  const onAddTagsForColoredPost = useCallback(
    async (props: any) => {
      if (props?.tagsIDs?.length > 0) {
        await urlApi().fetchTags(props?.tagsIDs).then((r: any) => {
          return r
        }).then((tags: any) => {
          modal.show('tags', {
            ctx: ["years"],
            global: true,
            value: tags,
            ...props,
          })
        })
      } else {
        modal.show('tags', {
          ctx: ["years"],
          global: true,
          value: [],
          ...props,
        })
      }

    },
    [modal]
  )

  const onBlockFeedLinkClick = useCallback(
    (url: string) => {
      modal.show('share', {
        customCopyUrl: url,
        images: [],
        title: feedShareTitle ?? '',
      })
    },
    [modal, feedShareTitle]
  )

  const onOpenTestModal = useCallback(
    (payload: {
      mode?: string
      editTestItemId?: string
      productKind?: 'test' | 'questionnaire'
      onComplete?: (data: { testItemId: string }) => void
    }) => {
      const { mode, onComplete, editTestItemId, productKind } = payload
      if (editTestItemId?.trim()) {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: productKind ?? 'test',
          longreadId,
          onFinish: onComplete,
          initialTestItemId: editTestItemId.trim(),
        })
        return
      }
      if (mode === 'new') {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: productKind ?? 'test',
          longreadId,
          onFinish: onComplete,
        })
      } else {
        modal.show('testSelectDraft', {
          longreadId,
          productKind: productKind ?? 'test',
        })
      }
    },
    [modal, longreadId]
  )

  const onOpenQuestionnaireModal = useCallback(
    (payload: {
      mode?: string
      editTestItemId?: string
      onComplete?: (data: { testItemId: string }) => void
    }) => {
      const { mode, onComplete, editTestItemId } = payload
      if (editTestItemId?.trim()) {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: 'questionnaire',
          longreadId,
          onFinish: onComplete,
          initialTestItemId: editTestItemId.trim(),
        })
        return
      }
      if (mode === 'existing') {
        modal.show('testSelectDraft', {
          longreadId,
          productKind: 'questionnaire',
          onFinish: onComplete,
        })
        return
      }
      modal.show('testCreate', {
        createMode: 'new',
        productKind: 'questionnaire',
        longreadId,
        onFinish: onComplete,
      })
    },
    [modal, longreadId]
  )

  const onCompleteTeachingEvaluation = useCallback(
    async (payload: {
      campaignId: string
      lmsModuleId?: string
      teamId?: string
      courseUrl?: string
      moduleName?: string
      onComplete?: () => void
    }) => {
      const accountId = profile?.accountId || 'anonymous'
      await teachingEvaluationApi.markCompleted(payload.campaignId, accountId, payload.lmsModuleId)
      payload.onComplete?.()
      modal.show('teaching-evaluation-complete', {
        teamId: payload.teamId,
        courseUrl: payload.courseUrl,
        campaignId: payload.campaignId,
      })
    },
    [modal, profile?.accountId]
  )

  const onLinkClick = useCallback(
    async (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!href.includes('/enroll-course/')) return
      e.preventDefault()

      const teamId = href.split('/enroll-course/')[1]?.split('?')[0]
      if (!teamId) return

      if (!token) {
        login()
        return
      }

      try {
        const team = await TeamAPI.Get({ idTeam: teamId }).then((r: any) => r.json())

        const lmsIsPaidCourse = (team?.lmsModulePrice ?? 0) > 0
        const courseName: string = Lang(team?.name, 'ru') || Lang(team?.name, 'en') || ''
        const courseUrl = `/teams/${team?.contextName || teamId}`
        const isMember =
          team?.membersIDs?.includes(profile?.accountId) ||
          team?.ownersIDs?.includes(profile?.accountId)

        if (isMember) {
          modal.show('enroll-course', { variant: 'already-member', courseName, courseUrl, teamId, isPaidCourse: lmsIsPaidCourse })
          return
        }

        if (lmsIsPaidCourse) {
          if (!team?.lmsCoursePaymentCardId) {
            modal.show('enroll-course', { variant: 'no-payment' })
            return
          }

          const educationUrl = process.env.REACT_APP_URL_MEDIIIA_COM
          const paymentPath = `/payment/course/${team?.lmsCoursePaymentCardId}/${teamId}`

          const isCurrentDomain = (() => {
            if (!educationUrl) return true
            try {
              return window.location.hostname === new URL(educationUrl).hostname
            } catch {
              return false
            }
          })()

          if (isCurrentDomain) {
            history.push(paymentPath)
          } else {
            try {
              window.location.href = `${new URL(educationUrl!).origin}${paymentPath}`
            } catch {
              history.push(paymentPath)
            }
          }
          return
        }

        const res = await API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/LMS/AddMemberToExternalCourse')
          .body({ teamId })
          .post()
          .send()

        if (!res.ok) {
          throw new Error(`AddMemberToExternalCourse ${res.status}`)
        }

        modal.show('enroll-course', { variant: 'success', courseName, courseUrl, teamId })
      } catch (err) {
        console.error('[onLinkClick] enroll-course error', err)
        toasts.error('К сожалению, пока нет возможности добавить Вас в участники')
      }
    },
    [modal, toasts, history, token, login, profile]
  )

  const onFormSubmitSuccess = useMemo(() => {
    const courseName: string = Lang(raw?.name, 'ru') || Lang(raw?.name, 'en') || ''
    const courseUrl = `/teams/${raw?.contextName || raw?.teamId}`
    const hasParentTeam = !!raw?.parentTeamId
    const lmsIsPaidCourse = (raw?.lmsModulePrice ?? 0) > 0
    const isMember =
          raw?.membersIDs?.includes(profile?.accountId) ||
          raw?.ownersIDs?.includes(profile?.accountId)
    
    if(hasParentTeam && isMember) {
      return () => modal.show('enroll-course', { variant: 'already-member', courseName: courseName, courseUrl: courseUrl, teamId: raw?.teamId, isPaidCourse: lmsIsPaidCourse })
    }

    if (hasParentTeam && !lmsIsPaidCourse) {
      return () => modal.show('form-course-success', { teamId: raw?.teamId })
    }
    return undefined
  }, [raw, modal])

  const onOpenLongreadTestResults = useCallback(
    async (payload: {
      testItemId?: string | null
      testData?: Record<string, unknown> | null
      language?: string
    }) => {
      let data = payload.testData ?? null
      const tid =
        payload.testItemId != null ? String(payload.testItemId).trim() : ''
      const accessToken = token?.trim()
      const lang = payload.language?.trim() || 'ru'

      if (tid && accessToken) {
        try {
          const raw = await fetchTestItem(tid, { accessToken })
          const fresh = unwrapTestItemPayload(raw)
          if (fresh) {
            data = mergeTestItemForResultsModal(
              payload.testData ?? data,
              fresh,
              lang
            )
          }
        } catch {
        }
      }

      if (!data) {
        return
      }

      const args = buildLongreadTestResultsModalArgsFromTestItem(data, lang)
      modal.show('longread-test-results', args)
    },
    [modal, token]
  )

  return useMemo(
    () => ({
      onOpenSetProjectsModal,
      onAddTagsForColoredPost,
      onBlockFeedLinkClick,
      onOpenTestModal,
      onOpenQuestionnaireModal,
      onCompleteTeachingEvaluation,
      onOpenLongreadTestResults,
      onLinkClick,
      onFormSubmitSuccess,
    }),
    [
      onOpenSetProjectsModal,
      onAddTagsForColoredPost,
      onBlockFeedLinkClick,
      onOpenTestModal,
      onOpenQuestionnaireModal,
      onCompleteTeachingEvaluation,
      onOpenLongreadTestResults,
      onLinkClick,
      onFormSubmitSuccess,
    ]
  )
}
import { Project, ProjectAPI, TeamAPI, API, Endpoints, Lang } from "@brg/api";
import { useAuth, useProfile } from "@brg/auth";
import { useModal } from "core"
import { useToasts } from "@brg/ui"
import { useHistory } from "react-router-dom"
import React, { useCallback, useMemo } from "react";
import { fetchTestItem, unwrapTestItemPayload } from "shared/api/testItem";
import { buildLongreadTestResultsModalArgsFromTestItem, mergeTestItemForResultsModal } from "shared/modals/longreadTestResults/buildLongreadTestResultsModalArgsFromTestItem";
import { teachingEvaluationApi } from 'shared/pages/CourseEdit/teachingEvaluation/api';
import urlApi from "shared/actions/urlApi";

interface IProps {
  id: string;
  isTeamLg?: boolean;
  raw: any;
  feedShareTitle?: string;
  longreadId?: string;
}

export const useGetExternalLGHandlers = ({
  raw,
  id,
  isTeamLg,
  feedShareTitle,
  longreadId,
}: IProps) => {
  const modal = useModal();
  const toasts = useToasts();
  const history = useHistory();
  const { token, login } = useAuth();
  const profile = useProfile();

  const onOpenSetProjectsModal = useCallback(async (props: any) => {
    if (isTeamLg) {
      modal.show('send-project-longread', {
        ...props,
        hideBookmark: true,
      })
      return;
    }
    if (raw) {
      await ProjectAPI.Get({ idProject: raw?.projectId ?? id })
        .then(
          (value) => value.json() as Promise<Project<'Global'> & { creatorId: string }>
        )
        .then((value) => {
          if (value.projectId) {
            modal.show('send-project-longread', {
              ...props,
              project: value,
              hideBookmark: true,
              ...(isTeamLg
                ? {
                    externalParams: {
                      authorOrCoAuthorIds: [],
                    },
                  }
                : {}),
            })
          }
        })
    }
  }, [raw, isTeamLg])

  const onAddTagsForColoredPost = useCallback(
    async (props: any) => {
      if (props?.tagsIDs?.length > 0) {
        await urlApi().fetchTags(props?.tagsIDs).then((r: any) => {
          return r
        }).then((tags: any) => {
          modal.show('tags', {
            ctx: ["years"],
            global: true,
            value: tags,
            ...props,
          })
        })
      } else {
        modal.show('tags', {
          ctx: ["years"],
          global: true,
          value: [],
          ...props,
        })
      }

    },
    [modal]
  )

  const onBlockFeedLinkClick = useCallback(
    (url: string) => {
      modal.show('share', {
        customCopyUrl: url,
        images: [],
        title: feedShareTitle ?? '',
      })
    },
    [modal, feedShareTitle]
  )

  const onOpenTestModal = useCallback(
    (payload: {
      mode?: string
      editTestItemId?: string
      productKind?: 'test' | 'questionnaire'
      onComplete?: (data: { testItemId: string }) => void
    }) => {
      const { mode, onComplete, editTestItemId, productKind } = payload
      if (editTestItemId?.trim()) {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: productKind ?? 'test',
          longreadId,
          onFinish: onComplete,
          initialTestItemId: editTestItemId.trim(),
        })
        return
      }
      if (mode === 'new') {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: productKind ?? 'test',
          longreadId,
          onFinish: onComplete,
        })
      } else {
        modal.show('testSelectDraft', {
          longreadId,
          productKind: productKind ?? 'test',
        })
      }
    },
    [modal, longreadId]
  )

  const onOpenQuestionnaireModal = useCallback(
    (payload: {
      mode?: string
      editTestItemId?: string
      onComplete?: (data: { testItemId: string }) => void
    }) => {
      const { mode, onComplete, editTestItemId } = payload
      if (editTestItemId?.trim()) {
        modal.show('testCreate', {
          createMode: 'new',
          productKind: 'questionnaire',
          longreadId,
          onFinish: onComplete,
          initialTestItemId: editTestItemId.trim(),
        })
        return
      }
      if (mode === 'existing') {
        modal.show('testSelectDraft', {
          longreadId,
          productKind: 'questionnaire',
          onFinish: onComplete,
        })
        return
      }
      modal.show('testCreate', {
        createMode: 'new',
        productKind: 'questionnaire',
        longreadId,
        onFinish: onComplete,
      })
    },
    [modal, longreadId]
  )

  const onCompleteTeachingEvaluation = useCallback(
    async (payload: {
      campaignId: string
      lmsModuleId?: string
      teamId?: string
      courseUrl?: string
      moduleName?: string
      onComplete?: () => void
    }) => {
      const accountId = profile?.accountId || 'anonymous'
      await teachingEvaluationApi.markCompleted(payload.campaignId, accountId, payload.lmsModuleId)
      payload.onComplete?.()
      modal.show('teaching-evaluation-complete', {
        teamId: payload.teamId,
        courseUrl: payload.courseUrl,
        campaignId: payload.campaignId,
      })
    },
    [modal, profile?.accountId]
  )

  const onLinkClick = useCallback(
    async (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!href.includes('/enroll-course/')) return
      e.preventDefault()

      const teamId = href.split('/enroll-course/')[1]?.split('?')[0]
      if (!teamId) return

      if (!token) {
        login()
        return
      }

      try {
        const team = await TeamAPI.Get({ idTeam: teamId }).then((r: any) => r.json())

        const lmsIsPaidCourse = (team?.lmsModulePrice ?? 0) > 0
        const courseName: string = Lang(team?.name, 'ru') || Lang(team?.name, 'en') || ''
        const courseUrl = `/teams/${team?.contextName || teamId}`
        const isMember =
          team?.membersIDs?.includes(profile?.accountId) ||
          team?.ownersIDs?.includes(profile?.accountId)

        if (isMember) {
          modal.show('enroll-course', { variant: 'already-member', courseName, courseUrl, teamId, isPaidCourse: lmsIsPaidCourse })
          return
        }

        if (lmsIsPaidCourse) {
          if (!team?.lmsCoursePaymentCardId) {
            modal.show('enroll-course', { variant: 'no-payment' })
            return
          }

          const educationUrl = process.env.REACT_APP_URL_MEDIIIA_COM
          const paymentPath = `/payment/course/${team?.lmsCoursePaymentCardId}/${teamId}`

          const isCurrentDomain = (() => {
            if (!educationUrl) return true
            try {
              return window.location.hostname === new URL(educationUrl).hostname
            } catch {
              return false
            }
          })()

          if (isCurrentDomain) {
            history.push(paymentPath)
          } else {
            try {
              window.location.href = `${new URL(educationUrl!).origin}${paymentPath}`
            } catch {
              history.push(paymentPath)
            }
          }
          return
        }

        const res = await API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/LMS/AddMemberToExternalCourse')
          .body({ teamId })
          .post()
          .send()

        if (!res.ok) {
          throw new Error(`AddMemberToExternalCourse ${res.status}`)
        }

        modal.show('enroll-course', { variant: 'success', courseName, courseUrl, teamId })
      } catch (err) {
        console.error('[onLinkClick] enroll-course error', err)
        toasts.error('К сожалению, пока нет возможности добавить Вас в участники')
      }
    },
    [modal, toasts, history, token, login, profile]
  )

  const onFormSubmitSuccess = useMemo(() => {
    const courseName: string = Lang(raw?.name, 'ru') || Lang(raw?.name, 'en') || ''
    const courseUrl = `/teams/${raw?.contextName || raw?.teamId}`
    const hasParentTeam = !!raw?.parentTeamId
    const lmsIsPaidCourse = (raw?.lmsModulePrice ?? 0) > 0
    const isMember =
          raw?.membersIDs?.includes(profile?.accountId) ||
          raw?.ownersIDs?.includes(profile?.accountId)
    
    if(hasParentTeam && isMember) {
      return () => modal.show('enroll-course', { variant: 'already-member', courseName: courseName, courseUrl: courseUrl, teamId: raw?.teamId, isPaidCourse: lmsIsPaidCourse })
    }

    if (hasParentTeam && !lmsIsPaidCourse) {
      return () => modal.show('form-course-success', { teamId: raw?.teamId })
    }
    return undefined
  }, [raw, modal])

  const onOpenLongreadTestResults = useCallback(
    async (payload: {
      testItemId?: string | null
      testData?: Record<string, unknown> | null
      language?: string
    }) => {
      let data = payload.testData ?? null
      const tid =
        payload.testItemId != null ? String(payload.testItemId).trim() : ''
      const accessToken = token?.trim()
      const lang = payload.language?.trim() || 'ru'

      if (tid && accessToken) {
        try {
          const raw = await fetchTestItem(tid, { accessToken })
          const fresh = unwrapTestItemPayload(raw)
          if (fresh) {
            data = mergeTestItemForResultsModal(
              payload.testData ?? data,
              fresh,
              lang
            )
          }
        } catch {
        }
      }

      if (!data) {
        return
      }

      const args = buildLongreadTestResultsModalArgsFromTestItem(data, lang)
      modal.show('longread-test-results', args)
    },
    [modal, token]
  )

  return useMemo(
    () => ({
      onOpenSetProjectsModal,
      onAddTagsForColoredPost,
      onBlockFeedLinkClick,
      onOpenTestModal,
      onOpenQuestionnaireModal,
      onCompleteTeachingEvaluation,
      onOpenLongreadTestResults,
      onLinkClick,
      onFormSubmitSuccess,
    }),
    [
      onOpenSetProjectsModal,
      onAddTagsForColoredPost,
      onBlockFeedLinkClick,
      onOpenTestModal,
      onOpenQuestionnaireModal,
      onCompleteTeachingEvaluation,
      onOpenLongreadTestResults,
      onLinkClick,
      onFormSubmitSuccess,
    ]
  )
}
